import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { useAddCommentMutation } from 'service/project/api/workItem';
import { useMediaUpload } from 'service/project/hooks/useMediaUpload';
import TipTapEditor from 'components/common/RichTextEditor/RichTextEditor';
import { WorkItemComment } from 'types/task';
import { ProjectUser } from 'types/project';
import { extractMentionIds } from 'utils/mentions';
import { getMediaType } from 'utils/helpers';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import { getInitials } from 'utils/getInitials';
import CommentThread from './CommentThread';

export type { WorkItemComment };

interface CommentsComponentProps {
  projectUuid: string;
  workItemUuid: string;
  projectKey: string;
  workItemKey: string;
  /** Root-level comments only (backend now excludes replies from this list) */
  existingComments: WorkItemComment[];
  mentionUsers?: ProjectUser[];
}

export default function CommentsComponent({
  projectUuid,
  workItemUuid,
  projectKey,
  workItemKey,
  existingComments,
  mentionUsers = [],
}: CommentsComponentProps) {
  const currentUser = useAppSelector(selectAuthUser);
  const { mutate: submitComment, isPending } = useAddCommentMutation();
  const { mutateAsync: uploadMedia } = useMediaUpload();
  const [newComment, setNewComment] = useState<string>('');

  const handleVideoUpload = async (file: File) => {
    const result = await uploadMedia({
      file,
      projectUuid,
      workItemUuid,
      originalFileName: file.name,
      mediaType: getMediaType(file),
    });
    return { mediaUuid: result.mediaUuid, projectUuid, fileName: file.name };
  };

  const isCommentEmpty =
    !newComment ||
    newComment === '<p></p>' ||
    (newComment.replace(/<[^>]*>?/gm, '').trim() === '' && !newComment.includes('<img'));

  const handleAddComment = (): void => {
    if (isCommentEmpty || isPending) return;
    submitComment(
      {
        commentUuid: crypto.randomUUID(),
        projectUuid,
        workItemUuid,
        commentDescription: newComment,
        mentionedAppUsersUuidList: extractMentionIds(newComment),
        // No parentCommentUuid → root comment
      },
      { onSuccess: () => setNewComment('') },
    );
  };

  return (
    <div className="p-0 rounded-lg relative">
      {/* ------------------------------------------------------------------ */}
      {/* Sticky global composer — for new root comments only                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="sticky top-0 z-20 bg-primary pb-2 mb-4 border-b border-primary-subtle pt-2">
        <div className="flex gap-3 mt-4">
          <div className="w-8 h-8 bg-primary-subtle rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
            {getInitials(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Y')}
          </div>
          <div className="flex-1 w-full border border-primary-subtle rounded-lg bg-primary-variant max-h-[300px] overflow-y-auto">
            <TipTapEditor
              initialContent={newComment}
              onChange={(content) => setNewComment(content)}
              placeholder="Add a comment... (Type @ to mention)"
              className={isPending ? 'opacity-50 pointer-events-none min-h-[60px]' : 'min-h-[0px]'}
              mentionUsers={mentionUsers}
              isComment={true}
              onVideoUpload={handleVideoUpload}
            />
          </div>
        </div>
        <div className="flex items-center justify-end mt-3 ml-11">
          <button
            onClick={handleAddComment}
            disabled={isCommentEmpty || isPending}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
              ${!isCommentEmpty && !isPending ? 'button-active' : 'button-inactive opacity-50 cursor-not-allowed'}
            `}
          >
            {isPending ? 'Posting...' : 'Comment'}
            {!isPending && <Send size={14} />}
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Root comment threads (each recursively renders its replies)         */}
      {/* ------------------------------------------------------------------ */}
      {existingComments.length > 0 ? (
        <div className="space-y-5 pb-10">
          {existingComments.map((comment) => (
            <CommentThread
              key={comment.commentUuid}
              comment={comment}
              projectUuid={projectUuid}
              workItemUuid={workItemUuid}
              projectKey={projectKey}
              workItemKey={workItemKey}
              mentionUsers={mentionUsers}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-neutral-medium">
          <p>No comments yet. Be the first to comment!</p>
        </div>
      )}
    </div>
  );
}
